import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { setDiscount } from "../../store/slices/IdSlice";

const baseUrl = import.meta.env.VITE_API_URL;

interface Coupon {
  code: string;
  creation_date: string;
  discount_type: "percentage" | "fixed"; // Assuming "percentage" or "fixed" as possible types
  discount_value: number;
  max_cap: number | 0;
  end_date: string;
  heading: string;
  id: number;
  img: string; // SVG data or URL string for the image
  imgAlt: string;
  is_active: boolean;
  max_usage: number;
  no_of_usage: number;
  start_date: string;
}

const Coupons: React.FC = () => {
  const dispatch = useDispatch();
  const totalAmount = useSelector(
    (state: { ID: { cartValuePrice: number } }) => state.ID.cartValuePrice
  );

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false); // State for the second modal
  const [inputValue, setInputValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const [couponsData, setCouponsData] = useState<Coupon[]>();

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const openTermsModal = (e: { preventDefault: () => void }) => {
    e.preventDefault();
    setIsTermsModalOpen(true);
  };

  const closeTermsModal = () => {
    setIsTermsModalOpen(false);
  };

  const generateDiscountText = (coupon: Coupon): string => {
    const isPercentage = coupon.discount_type === "percentage";
    const value = coupon.discount_value;
    const maxCap = coupon.max_cap || 0;

    if (isPercentage) {
      let percentageDiscount = (totalAmount * value) / 100;
      if (maxCap > 0 && percentageDiscount > maxCap) {
        percentageDiscount = maxCap;
      }
      return maxCap > 0
        ? `Save ${value}% (up to <span className="ff-currency ff-medium font-16"></span>  ${maxCap}) on this order`
        : `Save ${value}% on this order`;
    } else {
      return `Save <span className="ff-currency ff-medium font-16"></span>  ${value.toFixed(
        2
      )} on this order`;
    }
  };

  useEffect(() => {
    if (isModalOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isModalOpen]);

  const getCouponData = async () => {
    try {
      const response = await axios.get(`${baseUrl}/api/openRoute/coupons`, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (response.data) {
        setCouponsData(response.data.activeCoupons);
      }
    } catch (error) {
    }
  };
  const selectedCouponCode = sessionStorage.getItem("selectedCouponCode");
  const applyCoupon = (couponCode: string) => {
    setInputValue(couponCode);
    sessionStorage.setItem("selectedCouponCode", couponCode);
    const selectedCoupon = couponsData?.find((c) => c.code === couponCode);
    sessionStorage.setItem(
      "selectedCoupon",
      JSON.stringify(selectedCoupon?.id)
    );

    if (selectedCoupon) {
      let discountAmount = 0;

      if (selectedCoupon.discount_type === "percentage") {
        discountAmount = (totalAmount * selectedCoupon.discount_value) / 100;
        if (selectedCoupon.max_cap && discountAmount > selectedCoupon.max_cap) {
          discountAmount = selectedCoupon.max_cap;
        }
      } else {
        discountAmount = selectedCoupon.discount_value;
      }
      sessionStorage.setItem("discountAmount", JSON.stringify(discountAmount));
      dispatch(setDiscount({ discount: discountAmount }));
    }
    closeModal();
    closeTermsModal();
  };

  useEffect(() => {
    import("./Coupons.css");
    if (selectedCouponCode) applyCoupon(selectedCouponCode);
    getCouponData();
  }, []);
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      closeModal();
      closeTermsModal(); // Close both modals on escape
    }
  };

  return (
    <>
      {couponsData && couponsData?.length >= 1 && (
        <div className="d-flex gap-2  align-items-center Cart-offer-sec   my-3 p-3 p-md-4">
          <div className="">
            <p className="fw-semibold font-13 text-black mb-0">
              <svg
                width="18"
                height="18"
                viewBox="0 0 18 18"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="me-2"
              >
                <path
                  d="M9 0C9.76691 0 10.5014 0.306045 11.0406 0.847925L11.6689 1.47622C11.871 1.67719 12.1444 1.79011 12.4295 1.79036H13.3296C14.0936 1.79036 14.8262 2.09384 15.3664 2.63402C15.9066 3.1742 16.2101 3.90685 16.2101 4.67079V5.57092C16.2101 5.85536 16.3235 6.129 16.5224 6.32973L17.1507 6.95802C17.4197 7.22555 17.6332 7.54358 17.779 7.89386C17.9247 8.24414 17.9998 8.61976 18 8.99915C18.0002 9.37854 17.9254 9.75423 17.7799 10.1046C17.6345 10.455 17.4213 10.7733 17.1525 11.041L16.5242 11.6693C16.3232 11.8715 16.2103 12.1449 16.2101 12.4299V13.3301C16.2101 14.094 15.9066 14.8266 15.3664 15.3668C14.8262 15.907 14.0936 16.2105 13.3296 16.2105H12.4295C12.1453 16.2107 11.8727 16.323 11.6707 16.5228L11.0424 17.1511C10.7749 17.4201 10.4568 17.6336 10.1066 17.7794C9.75628 17.9251 9.38066 18.0003 9.00127 18.0004C8.62188 18.0006 8.2462 17.9258 7.89579 17.7804C7.54539 17.6349 7.22716 17.4217 6.9594 17.1529L6.33111 16.5246C6.12895 16.3237 5.85555 16.2107 5.5705 16.2105H4.67036C3.90643 16.2105 3.17378 15.907 2.6336 15.3668C2.09341 14.8266 1.78994 14.094 1.78994 13.3301V12.4299C1.78969 12.1458 1.67747 11.8731 1.47759 11.6711L0.849301 11.0428C0.58029 10.7753 0.366779 10.4573 0.221031 10.107C0.0752824 9.75671 0.00016756 9.38109 2.80015e-07 9.0017C-0.000167 8.62231 0.0746166 8.24662 0.220056 7.89622C0.365496 7.54581 0.578726 7.22759 0.847501 6.95982L1.47579 6.33153C1.67677 6.12938 1.78969 5.85598 1.78994 5.57092V4.67079L1.79444 4.50696C1.83628 3.77255 2.15751 3.08197 2.69226 2.57684C3.22701 2.07172 3.93476 1.79032 4.67036 1.79036H5.5705C5.85467 1.79012 6.1273 1.6779 6.32931 1.47802L6.9576 0.849725C7.22524 0.580485 7.54347 0.366809 7.89398 0.220982C8.24449 0.0751551 8.62037 5.49765e-05 9 0ZM11.2413 9.89155C10.8832 9.89155 10.5398 10.0338 10.2866 10.287C10.0334 10.5402 9.89113 10.8837 9.89113 11.2418C9.89113 11.5998 10.0334 11.9433 10.2866 12.1965C10.5398 12.4497 10.8832 12.592 11.2413 12.592C11.5994 12.592 11.9429 12.4497 12.1961 12.1965C12.4493 11.9433 12.5915 11.5998 12.5915 11.2418C12.5915 10.8837 12.4493 10.5402 12.1961 10.287C11.9429 10.0338 11.5994 9.89155 11.2413 9.89155ZM12.3278 5.65463C12.159 5.48588 11.9301 5.39109 11.6914 5.39109C11.4527 5.39109 11.2238 5.48588 11.055 5.65463L5.65421 11.0554C5.49024 11.2252 5.39951 11.4526 5.40156 11.6886C5.40362 11.9246 5.49828 12.1504 5.66517 12.3172C5.83206 12.4841 6.05783 12.5788 6.29384 12.5809C6.52985 12.5829 6.75723 12.4922 6.92699 12.3282L12.3278 6.92742C12.4965 6.75862 12.5913 6.52971 12.5913 6.29103C12.5913 6.05234 12.4965 5.82343 12.3278 5.65463ZM6.74067 5.39089C6.38257 5.39089 6.03914 5.53315 5.78593 5.78636C5.53272 6.03957 5.39047 6.383 5.39047 6.74109C5.39047 7.09919 5.53272 7.44261 5.78593 7.69583C6.03914 7.94904 6.38257 8.09129 6.74067 8.09129C7.09876 8.09129 7.44219 7.94904 7.6954 7.69583C7.94861 7.44261 8.09087 7.09919 8.09087 6.74109C8.09087 6.383 7.94861 6.03957 7.6954 5.78636C7.44219 5.53315 7.09876 5.39089 6.74067 5.39089Z"
                  fill="#29844D"
                />
              </svg>
              Coupons and offers
            </p>
            <p
              className="mb-0 font-12 text-black ff-medium pt-1"
              dangerouslySetInnerHTML={{
                __html: generateDiscountText(couponsData[0]),
              }}
            ></p>
          </div>

          <div
            className="flex-grow-1 text-align-end d-flex align-items-center justify-content-end"
            onKeyDown={handleKeyDown}
          >
            <button
              type="button"
              className="btn bg-none d-flex align-items-center justify-content-between font-13 ff-medium"
              aria-label="1 offer"
              onClick={openModal}
            >
              <span>{couponsData?.length} offer</span>
              <i
                className="fas fa-chevron-right ms-2"
                style={{ color: "#6E42E5" }}
              ></i>{" "}
              {/* Font Awesome icon */}
            </button>
            {isModalOpen && (
              <div
                className="modal d-block mb-fix slide-from-bottom"
                role="dialog"
                style={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}
              >
                <div
                  className="modal-dialog modal-dialog-centered modal-dialog-scrollable"
                  role="document"
                >
                  <div className="modal-content">
                    <div className="modal-header">
                      <button
                        type="button"
                        className="btn-close"
                        aria-label="Close"
                        onClick={closeModal}
                      ></button>
                    </div>
                    <div className="modal-body Coupon-modal">
                      <p className="modal-title ff-semibold font-18 text-black mb-3">
                        Coupons & Offers
                      </p>
                      <div className="position-relative form-floating-custom ">
                        <input
                          type="text"
                          className="form-control"
                          value={inputValue}
                          onChange={(e) => setInputValue(e.target.value)}
                          ref={inputRef}
                        />
                        <label className="form-label">Enter Coupon Code</label>
                        <span
                          className="position-absolute"
                          onClick={() => applyCoupon(inputValue)}
                          style={{
                            color: inputValue ? "rgb(110, 66, 229)" : "grey",
                            cursor: inputValue ? "pointer" : "not-allowed",
                            right: "10px",
                            top: "50%",
                            transform: "translateY(-50%)",
                            fontSize: "14px",
                            fontWeight: "600",
                          }}
                        >
                          Apply
                        </span>
                      </div>

                      <div className="border-top">
                        {couponsData?.map((offer, index) => (
                          <div key={index} className="mt-3 p-3  border-bottom ">
                            <div className="d-flex align-items-center gap-1 gap-md-3 flex-wrap">
                              <div className="cart-map-ico col-3 flex-shrink-0">
                                <img
                                  src="https://goodhand.b-cdn.net/Assets/cart/cart-flow/gift-main.svg"
                                  className="img-fluid"
                                  alt="Coupons"
                                />
                              </div>
                              <div className="d-flex align-items-start flex-column ">
                                <div className="d-flex align-items-center gap-2">
                                  <div className="d-flex align-items-center gap-2 bg-main px-2 rounded-3">
                                    <div>
                                      <svg
                                        width="11"
                                        height="11"
                                        viewBox="0 0 11 11"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                      >
                                        <path
                                          d="M5.04421 0C5.48175 9.34446e-05 5.90134 0.17398 6.21067 0.483413L10.4508 4.72359C10.8025 5.07527 11 5.55222 11 6.04954C11 6.54685 10.8025 7.0238 10.4508 7.37548L7.37548 10.4508C7.0238 10.8025 6.54685 11 6.04954 11C5.55222 11 5.07527 10.8025 4.72359 10.4508L0.483413 6.21067C0.17398 5.90134 9.34446e-05 5.48175 0 5.04421V2.19983C0 1.6164 0.231767 1.05686 0.644316 0.644316C1.05686 0.231767 1.6164 0 2.19983 0H5.04421ZM3.02477 1.92485C2.74727 1.92476 2.48 2.02957 2.27652 2.21825C2.07305 2.40694 1.94841 2.66556 1.9276 2.94227L1.92485 3.02477C1.92485 3.24231 1.98936 3.45497 2.11022 3.63585C2.23108 3.81673 2.40287 3.95771 2.60385 4.04096C2.80483 4.12421 3.02599 4.14599 3.23935 4.10355C3.45271 4.06111 3.6487 3.95635 3.80253 3.80253C3.95635 3.6487 4.06111 3.45271 4.10355 3.23935C4.14599 3.02599 4.12421 2.80483 4.04096 2.60385C3.95771 2.40287 3.81673 2.23108 3.63585 2.11022C3.45497 1.98936 3.24231 1.92485 3.02477 1.92485Z"
                                          fill="white"
                                        />
                                      </svg>
                                    </div>
                                    <div>
                                      <p className="font-12 mb-0 text-white">
                                        code :{" "}
                                        <span className="ff-semibold text-white">
                                          {offer.code}
                                        </span>
                                      </p>
                                    </div>
                                  </div>
                                </div>
                                <div className="font-12 discount ff-medium">
                                  {generateDiscountText(offer)}
                                </div>
                                <div className="font-12 discount ff-medium text-main">
                                  <a
                                    onClick={openTermsModal} // Open the second modal
                                    rel="nofollow"
                                    style={{
                                      cursor: "pointer",
                                      textDecoration: "none",
                                      color: "rgb(110, 66, 229)",
                                    }}
                                  >
                                    View T&C
                                  </a>
                                </div>
                              </div>
                              {/* <div className="ms-3">
                                <p className="mb-0 ff-semibold font-13">
                                  {offer.heading}
                                </p>
                                <p className="mb-0 text-muted font-13">
                                  Use: {offer.code}
                                </p>
                                <p className="mb-0 text-success font-13">
                                  Save AED 53 on this order
                                </p>
                                <a
                                  className="btn-link ff-semibold font-14"
                                  onClick={openTermsModal} // Open the second modal
                                  rel="nofollow"
                                  style={{
                                    cursor: "pointer",
                                    textDecoration: "none",
                                    color: "rgb(110, 66, 229)",
                                  }}
                                >
                                  View T&C
                                </a>
                              </div> */}
                              <div className="flex-grow-1 text-end">
                                {inputValue == offer.code ? (
                                  <p className="discount font-14 ff-medium d-flex gap-2 align-items-center justify-content-end">
                                    <svg
                                      width="11"
                                      height="12"
                                      viewBox="0 0 11 12"
                                      fill="none"
                                      xmlns="http://www.w3.org/2000/svg"
                                    >
                                      <path
                                        d="M5.5 0.5C5.92784 0.500026 6.34102 0.655872 6.6623 0.938401L6.74701 1.01816L7.13096 1.4021C7.23634 1.50679 7.37393 1.57287 7.52151 1.58968L7.59577 1.59408H8.14585C8.59537 1.59405 9.02788 1.76601 9.35467 2.07469C9.68145 2.38337 9.87776 2.80537 9.90333 3.25417L9.90608 3.35428V3.90434C9.90608 4.05286 9.95668 4.19753 10.048 4.31304L10.0975 4.36805L10.4809 4.75199C10.7987 5.06797 10.984 5.49327 10.999 5.94115C11.014 6.38904 10.8577 6.8258 10.5618 7.16237L10.482 7.24708L10.0981 7.63102C9.99337 7.73639 9.92728 7.87399 9.91048 8.02157L9.90608 8.09583V8.64589C9.9061 9.09541 9.73414 9.52791 9.42546 9.85469C9.11677 10.1815 8.69476 10.3778 8.24596 10.4033L8.14585 10.4061H7.59577C7.44746 10.4061 7.30349 10.4561 7.18707 10.548L7.13206 10.5975L6.74811 10.9809C6.43213 11.2987 6.00683 11.484 5.55893 11.499C5.11103 11.514 4.67427 11.3577 4.3377 11.0618L4.25299 10.982L3.86904 10.5981C3.76366 10.4934 3.62607 10.4273 3.47849 10.4105L3.40423 10.4061H2.85415C2.40463 10.4061 1.97212 10.2342 1.64533 9.92548C1.31855 9.6168 1.12224 9.1948 1.09667 8.746L1.09392 8.64589V8.09583C1.09388 7.94752 1.04388 7.80355 0.952006 7.68713L0.902499 7.63212L0.519099 7.24818C0.201312 6.9322 0.0160158 6.5069 0.000990542 6.05901C-0.0140347 5.61112 0.142343 5.17437 0.438238 4.8378L0.517999 4.75309L0.901949 4.36915C1.00663 4.26378 1.07272 4.12618 1.08952 3.9786L1.09392 3.90434V3.35428L1.09667 3.25417C1.12124 2.82261 1.30374 2.41518 1.60939 2.10953C1.91504 1.80388 2.32248 1.62139 2.75404 1.59683L2.85415 1.59408H3.40423C3.55254 1.59403 3.69651 1.54404 3.81293 1.45216L3.86794 1.40265L4.25189 1.01926C4.41544 0.85473 4.60991 0.724154 4.82411 0.63504C5.0383 0.545927 5.26801 0.500034 5.5 0.5ZM7.53362 4.50556C7.43046 4.40244 7.29057 4.34451 7.14472 4.34451C6.99886 4.34451 6.85897 4.40244 6.75581 4.50556L4.94443 6.31637L4.23318 5.60569L4.18148 5.56003C4.07092 5.47455 3.93196 5.43435 3.79284 5.4476C3.65371 5.46085 3.52485 5.52657 3.43242 5.63139C3.33999 5.73621 3.29092 5.87229 3.29519 6.01197C3.29945 6.15166 3.35673 6.28449 3.45538 6.38348L4.55553 7.48361L4.60723 7.52926C4.71307 7.61136 4.84522 7.65202 4.97891 7.64361C5.11259 7.63521 5.23861 7.57831 5.33333 7.48361L7.53362 5.28335L7.57927 5.23165C7.66137 5.12581 7.70203 4.99366 7.69363 4.85998C7.68522 4.7263 7.62833 4.60028 7.53362 4.50556Z"
                                        fill="#2B932F"
                                      />
                                    </svg>
                                    Applied
                                  </p>
                                ) : (
                                  <a
                                    className="btn-link ff-semibold font-14"
                                    rel="nofollow"
                                    onClick={() => applyCoupon(offer.code)}
                                    style={{
                                      cursor: "pointer",
                                      textDecoration: "none",
                                      marginLeft: "1.3rem",
                                      color: "rgb(110, 66, 229)",
                                    }}
                                  >
                                    Apply
                                  </a>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {isTermsModalOpen && (
              <div
                className="modal d-block"
                role="dialog"
                style={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}
              >
                <div
                  className="modal-dialog modal-dialog-centered modal-dialog-scrollable"
                  role="document"
                >
                  <div className="modal-content">
                    <div className="modal-header">
                      <button
                        type="button"
                        className="btn-close"
                        aria-label="Close"
                        onClick={closeTermsModal}
                      ></button>
                    </div>
                    <div className="modal-body cp-modal-2  bg-white p-4">
                      <p className="modal-title ff-semibold font-18 text-black">
                        Terms And Conditions
                      </p>
                      <ul className="font-14 m-4">
                        <li>Lorem ipsum dolor sit amet.</li>
                        <li>
                          Sed do eiusmod tempor incididunt ut labore et dolore
                          magna aliqua.
                        </li>
                        <li>Ut enim ad minim veniam, quis nostrud</li>
                      </ul>

                      <button
                        type="button"
                        className="btn btn-primary w-100"
                        onClick={closeTermsModal}
                      >
                        Okay got it
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default Coupons;
